import { LevelPhase, LevelSim, SimConstants } from "@highmarch/domain";
import { icon } from "./iconsKit";
import { WavePreviewView } from "./wavePreview";
import { Settings } from "./settings";
import { setText } from "./hudPills";
import type { HudHandlers } from "./hud";
import { waveTitle } from "./runMode";

/** The game speeds the speed button cycles through. */
export const GAME_SPEEDS: readonly number[] = [1, 2, 3];

/** Where a remembered speed sits in GAME_SPEEDS; anything else (an old save, a hand-edited value) starts at ×1. */
export function speedIndexOf(speed: unknown): number { return Math.max(0, GAME_SPEEDS.indexOf(speed as number)); }

/** The pause glyph: two filled bars on the icon kit's 24 px grid (the generated kit has no pause icon). */
function pauseIcon(size: number): string {
  return `<svg class="ico" width="${size}" height="${size}" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1.2"/><rect x="14" y="5" width="4" height="14" rx="1.2"/></svg>`;
}

/**
 * The HUD's bottom control row (hud.ts): START WAVES / NEXT WAVE, pause and speed, the wave plate and the rally flag,
 * with the wave preview and the rally hint riding on it; where it sits over the tower sheet or the card tray, and when
 * it is hidden. The speed the level runs at is decided here.
 */
export class HudControls {
  readonly root: HTMLDivElement;
  /** What the next wave brings (or what is still to come), docked above the control row. */
  private readonly preview = new WavePreviewView();
  /**
   * The rally flag's line over the control row (QoL C5): the prompt while the flag button is armed, then a short word
   * on what the tap did. It stands where the wave strip does (the strip steps aside while it shows).
   */
  private readonly rallyHint: HTMLDivElement;
  /**
   * CALL during a running wave (M30 #17, Kingdom Rush style): the next stage joins the one on the road and pays the
   * time skipped ("CALL +35"). Docked over the row's right end, shown only while the sim says it may be called.
   */
  private readonly callBtn: HTMLButtonElement;
  private callBonusShown = -1;
  private rallyArmed = false;
  private rallyNoteTimer = 0;
  /** What the armed flag button asks for. */
  static readonly RallyPrompt = "Tap the road to rally your troops";
  /** Starts at the speed the player last chose (Settings.speed), so ×3 carries from level to level. */
  private speedIndex = speedIndexOf(Settings.current.speed);
  private paused = false;
  /** Packs the countdown seconds and the early-call bonus into one "last written" key (the bonus stays far below it). */
  static readonly CountKeyStride = 100000;

  constructor(private readonly h: HudHandlers) {
    this.root = document.createElement("div");
    this.root.id = "controls";
    this.rallyHint = document.createElement("div");
    this.rallyHint.className = "rally-hint plate hidden";
    this.rallyHint.setAttribute("role", "status");
    this.callBtn = document.createElement("button");
    this.callBtn.className = "btn gold disp call hidden";
    this.callBtn.dataset.guide = "call";
    this.callBtn.setAttribute("aria-label", "Call the next wave now for bonus points");
    this.callBtn.innerHTML = `${icon("play", 16)} <span class="lbl">CALL</span> <b class="bonus"></b>`;
    this.callBtn.addEventListener("click", () => this.h.onStart());
    this.preview.onChip = (a, boss, imm) => h.onEnemyChip?.(a, boss, imm);
  }

  /** The row's shape, and the wave preview under it, for this frame of the sim. */
  refresh(sim: LevelSim): void {
    this.refreshRow(sim);
    this.preview.refresh(sim);
  }

  /**
   * The control row is rebuilt only when its *shape* changes (phase, offer open, speed); the countdown, enemies-left
   * count and wave bar are written into the existing nodes so no frame ever shows a freshly created, empty element.
   */
  private refreshRow(sim: LevelSim): void {
    const offerOpen = sim.currentOffer !== null;
    const speed = this.speed;
    // A card offered mid-battle takes the bottom of the screen, but pause and speed stay: they ride on the tray's top
    // edge like on the tower sheet's (placeControls), so the game can always be paused. The opening draft has neither.
    const inBattle = sim.phase === LevelPhase.BetweenWaves || sim.phase === LevelPhase.WaveRunning;
    const shape = offerOpen && sim.phase !== LevelPhase.Ready ? (inBattle ? `offer|${speed}` : "") : `${sim.phase}|${speed}|${this.undoable}`;
    if (shape !== this.shape) {
      this.shape = shape;
      this.root.dataset.shape = shape;
      const speedBtn = `${this.pauseButtonHtml()}<button class="btn dark disp speed" data-guide="speed" aria-label="Game speed">${icon("fast", 18)} ×${speed}</button>`;
      let html = "";
      if (shape.startsWith("offer")) html = speedBtn;
      else switch (sim.phase) {
        case LevelPhase.Ready: html = `${this.undoable ? `<button class="btn dark disp undo" data-guide="undo" aria-label="Undo the last card">${icon("back", 18)} UNDO</button>` : ""}<button class="btn gold disp start accent" data-guide="start">${icon("play", 20)} START WAVES</button>`; break;
        case LevelPhase.BetweenWaves: html = shape ? `${speedBtn}<button class="btn gold disp start accent" data-guide="start">${icon("play", 20)} <span class="lbl">NEXT WAVE</span><b class="bonus"></b></button>` : ""; break;
        case LevelPhase.WaveRunning: html = shape ? `${speedBtn}<div class="plate wave"><b class="disp gold"></b><small></small><div class="bar"><div class="fill gold" style="width:0%"></div></div></div><button class="icbtn flag${this.rallyArmed ? " on" : ""}" data-guide="flag" aria-label="Move the rally flag" aria-pressed="${this.rallyArmed}">${icon("flag", 22)}</button>` : ""; break;
        default: html = "";
      }
      this.root.innerHTML = html;
      this.root.append(this.preview.root);            // rides with the row: hidden whenever the row is
      this.root.append(this.rallyHint);
      this.root.append(this.callBtn);
      this.shownCount = -1;
      this.controlsEmpty = html === "";
      this.syncControlsHidden();
      this.placeControls();
      this.root.querySelector<HTMLButtonElement>(".start")?.addEventListener("click", () => this.h.onStart());
      this.root.querySelector<HTMLButtonElement>(".speed")?.addEventListener("click", () => {
        // Under the walkthrough's ×1 the next speed is the one after what the button shows, and the override is over.
        const from = this.speedOverride !== null ? speedIndexOf(this.speedOverride) : this.speedIndex;
        this.speedOverride = null;
        this.speedIndex = (from + 1) % GAME_SPEEDS.length;
        Settings.set({ speed: GAME_SPEEDS[this.speedIndex] });
        this.h.onSpeed(GAME_SPEEDS[this.speedIndex]);
        this.refreshRow(sim);
      });
      this.root.querySelector<HTMLButtonElement>(".pause")?.addEventListener("click", () => this.h.onPause?.());
      this.root.querySelector<HTMLButtonElement>(".undo")?.addEventListener("click", () => this.h.onUndo?.());
      this.root.querySelector<HTMLButtonElement>(".flag")?.addEventListener("click", () => this.h.onRally?.());
    }
    if (!shape) return;
    this.refreshCall(sim);
    if (sim.phase === LevelPhase.BetweenWaves) {
      // Calling early pays for the countdown it cuts (DESIGN §4.8.7): the button shows what a tap earns now.
      const secs = Math.ceil(sim.countdown / SimConstants.TicksPerSecond);
      const bonus = sim.earlyCallReward;
      const key = secs * HudControls.CountKeyStride + bonus;
      if (key !== this.shownCount) {
        this.shownCount = key;
        setText(this.root.querySelector(".start .lbl"), `NEXT WAVE · ${secs}s`);
        setText(this.root.querySelector(".start .bonus"), bonus > 0 ? `+${bonus}` : "");
      }
    } else if (sim.phase === LevelPhase.WaveRunning) {
      const left = sim.aliveEnemies + Math.max(0, sim.scheduledThisStage - sim.spawnedThisStage);
      const key = left * 1000 + sim.stage;
      if (key === this.shownCount) return;                  // the common frame: nothing on the wave plate moved
      this.shownCount = key;
      const done = sim.scheduledThisStage > 0 ? 1 - left / sim.scheduledThisStage : 0;
      setText(this.root.querySelector(".wave b"), waveTitle(sim));
      setText(this.root.querySelector(".wave small"), `${left} raider${left === 1 ? "" : "s"} left`);
      const fill = this.root.querySelector<HTMLElement>(".wave .fill");
      const w = `${Math.round(done * 100)}%`;
      if (fill && fill.style.width !== w) fill.style.width = w;
    }
  }
  /** The CALL button during a running wave: shown while the sim takes a call, with what a tap pays now. */
  private refreshCall(sim: LevelSim): void {
    const bonus = sim.phase === LevelPhase.WaveRunning ? sim.earlyCallBonus() : 0;
    if (bonus === this.callBonusShown) return;
    this.callBonusShown = bonus;
    this.callBtn.classList.toggle("hidden", bonus <= 0);
    this.root.classList.toggle("can-call", bonus > 0);
    if (bonus > 0) setText(this.callBtn.querySelector(".bonus"), `+${bonus}`);
  }

  /** The speed the HUD shows (and the level runs at): the walkthrough's override, or the player's own. */
  get speed(): number { return this.speedOverride ?? GAME_SPEEDS[this.speedIndex]; }

  /**
   * The level-1 walkthrough runs at ×1 whatever Settings says (DESIGN §7): the button shows `n` until this is lifted
   * (null) or the player taps it. Settings.speed is untouched. The row picks it up on its next refresh.
   */
  setSpeedOverride(n: number | null): void { this.speedOverride = n; }
  private speedOverride: number | null = null;

  /** The pause button's look: bars while running, a play arrow while paused. Updated in place (no row rebuild). */
  setPaused(on: boolean): void {
    if (on === this.paused) return;
    this.paused = on;
    const b = this.root.querySelector<HTMLButtonElement>(".pause");
    if (b) b.outerHTML = this.pauseButtonHtml();
    this.root.querySelector<HTMLButtonElement>(".pause")?.addEventListener("click", () => this.h.onPause?.());
  }
  /**
   * The flag button armed (the next map tap plants the rally flag) or not: the button lights gold and the prompt
   * shows over the row. Updated in place; a row rebuild keeps it (refreshControls reads rallyArmed).
   */
  setRallyArmed(on: boolean): void {
    if (on === this.rallyArmed) return;
    this.rallyArmed = on;
    const b = this.root.querySelector<HTMLButtonElement>(".flag");
    b?.classList.toggle("on", on);
    b?.setAttribute("aria-pressed", String(on));
    window.clearTimeout(this.rallyNoteTimer);
    this.showRallyHint(on ? HudControls.RallyPrompt : null);
  }
  /** A short word over the row on what the flag did ("Troops rallying…", "Cancelled"), for `ms`. */
  rallyNote(text: string, ms: number): void {
    window.clearTimeout(this.rallyNoteTimer);
    this.showRallyHint(text);
    this.rallyNoteTimer = window.setTimeout(() => this.showRallyHint(this.rallyArmed ? HudControls.RallyPrompt : null), ms);
  }
  private showRallyHint(text: string | null): void {
    if (text !== null) setText(this.rallyHint, text);
    this.rallyHint.classList.toggle("hidden", text === null);
    this.root.classList.toggle("rallying", text !== null);
  }

  private pauseButtonHtml(): string {
    return `<button class="btn dark disp pause${this.paused ? " on" : ""}" data-guide="pause" aria-label="${this.paused ? "Resume" : "Pause"}" aria-pressed="${this.paused}">${this.paused ? icon("play", 20) : pauseIcon(20)}</button>`;
  }

  /**
   * The tower sheet owns the bottom of the screen: the row lifts to sit `px` above the bottom edge (on the sheet's top)
   * and shows only the pause and speed buttons, which the player still wants while reading a tower. null drops it back.
   */
  setSheetLift(px: number | null): void {
    this.sheetLift = px;
    this.placeControls();
  }
  private sheetLift: number | null = null;
  /** The card tray's top (px above the bottom edge) while it is up, from setClusterLift. */
  private trayLift: number | null = null;

  /**
   * Where the row sits: on the tower sheet's top edge while one is open, on the card tray's while a mid-battle offer
   * shows only pause and speed (the `offer` shape), else at the bottom. `over-sheet` shows just those two buttons and
   * lets the rest of the row's width pass touches to the map. Written only on a change.
   */
  private placeControls(): void {
    const px = this.sheetLift ?? ((this.shape ?? "").startsWith("offer") ? this.trayLift : null);
    const lift = px === null ? "" : `${Math.round(px + HudControls.SheetGap)}px`;
    if (this.root.style.bottom !== lift) this.root.style.bottom = lift;
    this.root.classList.toggle("over-sheet", px !== null);
  }
  /** Gap (px) between the lifted pause/speed buttons and the tower sheet's top edge. */
  static readonly SheetGap = 8;

  /** Whether UNDO (D17) is offered beside START WAVES; the row picks it up on its next refresh (it is in the shape). */
  setUndoable(on: boolean): void { this.undoable = on && this.h.onUndo !== undefined; }
  private undoable = false;

  /**
   * Holds the control row hidden while something else owns the bottom of the screen (the level end; the tower sheet
   * only lifts it, see setSheetLift).
   * The row's visibility is decided only here and in refreshControls, from both inputs, so lifting the hold brings the
   * row back even when its shape did not change in the meantime (closing a tower sheet mid-wave left it hidden).
   */
  setControlsSuppressed(on: boolean): void {
    if (on === this.controlsSuppressed) return;
    this.controlsSuppressed = on;
    this.syncControlsHidden();
  }
  private controlsSuppressed = false;
  /** The current shape has no controls (the opening draft, an offer on the table, the level over). */
  private controlsEmpty = true;
  private syncControlsHidden(): void { this.root.classList.toggle("hidden", controlsHidden(this.controlsEmpty, this.controlsSuppressed)); }

  /** The control row's shape, and the countdown or wave-progress value it last wrote (-1 after a rebuild). */
  private shape: string | undefined = undefined;
  private shownCount = -1;

  /**
   * The card tray's top (px above the bottom edge) while it is up, or null: the row rides on it while a mid-battle offer
   * shows only pause and speed. Re-placed only on a change.
   */
  setTrayLift(px: number | null): void { if (px !== this.trayLift) { this.trayLift = px; this.placeControls(); } }

  /** The pause and speed buttons' box while they ride on the tower sheet or the card tray (null otherwise), for the edge markers. */
  liftedRect(side: string): ScreenRect | null {
    return !this.root.classList.contains("over-sheet") || this.root.classList.contains("hidden") ? null
      : this.liftedRowRect.get(`${this.root.style.bottom}|${this.shape}|${this.paused}|${side}`,
        () => [...this.root.querySelectorAll<HTMLElement>(":scope > .pause, :scope > .speed")].map((b) => b.getBoundingClientRect()));
  }
  private readonly liftedRowRect = new MeasuredRect();

  dispose(): void { window.clearTimeout(this.rallyNoteTimer); this.root.remove(); }
}

/** Whether the bottom control row is hidden: it has nothing to show, or the level is holding it back. */
export function controlsHidden(empty: boolean, suppressed: boolean): boolean { return empty || suppressed; }

/** A screen rectangle in CSS px (the edge markers' band and what they avoid). */
export interface ScreenRect { readonly left: number; readonly top: number; readonly right: number; readonly bottom: number }

/** The union of some elements' boxes, re-measured only when `key` changes (and until they have been laid out). */
export class MeasuredRect {
  private key = "";
  private box: ScreenRect | null = null;
  get(key: string, measure: () => readonly DOMRect[]): ScreenRect | null {
    if (key === this.key) return this.box;
    const rs = measure().filter((r) => r.width > 0);
    if (rs.length === 0) return null;                   // not laid out yet: measure again next time
    this.key = key;
    this.box = { left: Math.min(...rs.map((r) => r.left)), top: Math.min(...rs.map((r) => r.top)), right: Math.max(...rs.map((r) => r.right)), bottom: Math.max(...rs.map((r) => r.bottom)) };
    return this.box;
  }
}
